import { getApiQueryKey, useGetApiData, usePostApiData } from "shared/datalayer";
import MyExpApi from "../../enum/MyExpApi";
import ApiRegistrationFormForTicketUpgrade from "shared/types/ApiRegistrationFormForTicketUpgrade";
import { ApiRegistrationParticipantForParticipantResponse } from "../../../../shared/types/ApiRegistrationParticipantForParticipantResponse";
import { ApiRegRequest } from "../../../console/modules/registration/approval/details/types";
import { QueryClient, useQueryClient } from "react-query";
import { ApiPayForPurchaseOrderResponse } from "../../../../shared/types/ApiPayForPurchaseOrderResponse";
import ApiRegistrationParticipantTicketUpgradeFormResponse from "../../../../shared/types/ApiRegistrationParticipantTicketUpgradeFormResponse";
import { isPaymentReceiptPendingForPO } from "apps/myExperience/utils";

export function useGetApplicableFormsList(registrationParticipantId?: string, errorHandler?: () => void) {
  return useGetApiData<Array<ApiRegistrationFormForTicketUpgrade>>(
    MyExpApi.TICKET_CHANGE_FORMS_LIST,
    {
      params: { registrationParticipantId }
    },
    { enabled: Boolean(registrationParticipantId), onError: errorHandler }
  );
}

function getTicketChangeFormDetailQueryKey(registrationParticipantId?: string, formId?: string) {
  return getApiQueryKey(MyExpApi.TICKET_CHANGE_GET_FORM_DETAIL, {
    params: { upgradeRegistrationFormId: formId, registrationParticipantId: registrationParticipantId }
  });
}

export function useGetApplicableFormDetails(props: {
  registrationParticipantId?: string;
  formId?: string;
  onSuccessHandler?: (data: ApiRegistrationParticipantTicketUpgradeFormResponse) => void;
  onErrorHandler?: () => void;
}) {
  return useGetApiData<ApiRegistrationParticipantTicketUpgradeFormResponse>(
    MyExpApi.TICKET_CHANGE_GET_FORM_DETAIL,
    {
      params: { upgradeRegistrationFormId: props.formId, registrationParticipantId: props.registrationParticipantId }
    },
    {
      enabled: Boolean(props.registrationParticipantId) && Boolean(props.formId),
      onSuccess: props.onSuccessHandler,
      onError: props.onErrorHandler
    }
  );
}

export function updateFormDetailsOnPaymentSuccess(props: {
  queryClient: QueryClient;
  registrationParticipantId?: string;
  formId?: string;
  paymentResponse: ApiPayForPurchaseOrderResponse;
}) {
  props.queryClient.setQueryData(
    getTicketChangeFormDetailQueryKey(props.registrationParticipantId, props.formId),
    (oldData): ApiRegistrationParticipantTicketUpgradeFormResponse => {
      const existingData = (oldData || {}) as ApiRegistrationParticipantTicketUpgradeFormResponse;
      return {
        ...existingData,
        registrationParticipantForm: {
          ...existingData.registrationParticipantForm,
          regPurchaseOrder: props.paymentResponse.purchaseOrder,
          myExperienceLoginUrl: props.paymentResponse.myExperienceLoginUrl
        },
        registrationFormForTicketUpgrade: {
          ...existingData.registrationFormForTicketUpgrade,
          pendingPurchaseOrder: isPaymentReceiptPendingForPO(props.paymentResponse.purchaseOrder)
            ? props.paymentResponse.purchaseOrder
            : undefined
        }
      };
    }
  );
}

export function useInitiateTicketChange(registrationParticipantId?: string, formId?: string) {
  const queryClient = useQueryClient();
  return usePostApiData<ApiRegistrationParticipantForParticipantResponse, ApiRegRequest>(
    MyExpApi.INITIATE_TICKET_CHANGE,
    {
      onSuccess: data => {
        queryClient.setQueryData(
          getTicketChangeFormDetailQueryKey(registrationParticipantId, formId),
          (oldData): ApiRegistrationParticipantTicketUpgradeFormResponse => {
            const existingData = (oldData || {}) as ApiRegistrationParticipantTicketUpgradeFormResponse;
            return { ...existingData, registrationParticipantForm: data };
          }
        );
      }
    }
  );
}
